import dayjs, { type OptionType } from 'dayjs';

import { DEFAULT_EMPTY_VALUE, DEFAULT_FORMAT, SIMPLE_DATE_FORMAT } from '@/constants';
import type { DayjsProps } from '@/utils/dayjsCs';
import { dayjsCs } from '@/utils/dayjsCs';
import { DATE_FORMAT } from '@/utils/format-helpers';
import { isValueEmpty } from '@/utils/value';

/**
 * Parses a date string in the format of DD/MM/YYYY and returns a Date object.
 * Returns null if the input is empty or invalid.
 * @returns A Date object or null if the input is empty or invalid.
 * @param dateString
 */
export const stringToDate = (dateString: string): Date | null => {
  if (isValueEmpty(dateString)) {
    return null;
  }
  return dayjs(dateString, DATE_FORMAT).toDate();
};

/**
 * Strict version of stringToDate, always returns a Date object
 * @returns A Date object.
 * @param dateString
 */
export const stringToDateRequired = (dateString?: string): Date => {
  return dayjs(dateString, DATE_FORMAT).toDate();
};

/**
 * Parses a date string in ISO 8601 format and returns a Date object in UTC timezone.
 * Returns null if the input is empty or invalid.
 * @returns A Date object in UTC timezone or null if the input is empty or invalid.
 * @param dateString
 */
export const stringToDateISO = (dateString: string): Date | null => {
  if (isValueEmpty(dateString)) {
    return null;
  }

  const parsedDate = dayjs(dateString).utc();

  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate.toDate();
};

/**
 * Transforms a date into a simple format(DD-MM-YYYY)
 * @returns A string representing the date in DD-MM-YYYY format.
 * @param date
 */
export const csSimpleDate = (date: Date | string | number): string => {
  return dayjs.tz(date, 'Europe/Prague').format(SIMPLE_DATE_FORMAT);
};

/**
 * Strict version of stringToDateISO, always returns a Date object in Europe/Prague timezone
 * @returns A Date object in Europe/Prague timezone.
 * @param date
 */
export const csSimpleDateDate = (date: Date | string | number): Date => {
  const parsedDate = dayjs(date).utc();
  return parsedDate.tz('Europe/Prague').toDate();
};

/**
 * Parses a date using dayjsCs and returns a Date object in Europe/Prague timezone.
 * Returns undefined if the input is empty or invalid.
 * @returns A Date object in Europe/Prague timezone or undefined if the input is empty or invalid.
 * @param date
 * @param inputFormat
 */
export const dateCsOptional = (date?: DayjsProps | null, inputFormat?: OptionType): Date | undefined => {
  if (isValueEmpty(date)) {
    return undefined;
  }

  try {
    return dayjs(date, inputFormat).tz('Europe/Prague').toDate();
  } catch {
    return undefined;
  }
};

/**
 * @deprecated Use apiDateToHumanReadableCs instead.
 */
export const apiDateToHumanReadable = (
  date: string,
  includeTime: boolean = false,
): string => {
  const parsedDate = dayjs.utc(date).tz('Europe/Prague');

  return parsedDate.format(
    includeTime ? 'DD. MM. YYYY HH:mm' : 'DD. MM. YYYY',
  );
};

/**
 * Transforms a date string from API into a human-readable format.
 * Very flexible with input parameters.
 * @param dateString - The date to Transform. Can be a string, Date, Dayjs object, or null.
 * @param format - The date format to use. Default is 'DD. MM. YYYY
 * @param includeTime - Whether to include time in the output. Default is false.
 * @param emptyValue - The value to return if the date is null or invalid. Default is '-'
 * @returns String representing the time in DD. MM. YYYY format if includeTime is false. Format can be customized, but HH:mm will be appended if includeTime is true.
 * Example: 25. 12. 2023 or 25. 12. 2023 14:30
 */
export function apiDateToHumanReadableCs(
  dateString: DayjsProps | null,
  format: string = DEFAULT_FORMAT,
  includeTime: boolean = false,
  emptyValue: string = DEFAULT_EMPTY_VALUE,
): string {
  if (!dateString) {
    return emptyValue;
  }

  const parsedDate = dayjsCs(dateString);

  if (!parsedDate.isValid()) {
    return emptyValue;
  }
  const finalFormat = includeTime ? `${format} HH:mm` : format;

  return parsedDate.format(finalFormat);
}

/**
 * @deprecated Use apiDateToHumanReadableCs instead.
 */
export const dateToHumanReadable = (date?: string | Date | number): string => {
  const returnDate = dayjs(date);
  return returnDate.format('DD. MM. YYYY');
};

/**
 * Transforms a iso date into a simple format(YYYY-MM-DD)
 * @param date - The Date to Transform
 * @returns String representing the date in YYYY-MM-DD format.
 * Example: 2023-12-25
 */
export const formatIsoToSimpleDate = (date: Date): string => {
  return dayjs(date).format('YYYY-MM-DD');
};

/**
 * Combines the date part of the first Date object with the time part of the second Date object.
 * @returns A new Date object with the combined date and time.
 * @param date
 * @param time
 */
export const combineDateAndTime = (date: Date, time: Date): Date => {
  const date1 = dayjs(date);
  const date2 = dayjs(time);

  return date1
    .hour(date2.hour())
    .minute(date2.minute())
    .second(date2.second())
    .millisecond(date2.millisecond())
    .toDate();
};

/**
 * Transforms a date into a time string in the format of HH:mm
 * @param date - The Date or date string to Transform
 * @returns String representing the time in HH:mm format.
 * Example: 14:30
 */
export const dateToTime = (date: Date | string | number): string => {
  return dayjs(date).format('HH:mm');
};

/**
 * Formats a date to 'YYYY-MM-DDT00:00:00±HH:MM' format with the local timezone offset.
 * @param date - The Date to format.
 * @returns A string representing the date in the specified format with timezone offset.
 */
export const formatDateTimezone = (date: Date): string => {
  const offset = date.getTimezoneOffset();
  const sign = offset > 0 ? '-' : '+';
  const absOffset = Math.abs(offset);
  const hours = String(Math.floor(absOffset / 60)).padStart(2, '0');
  const minutes = String(absOffset % 60).padStart(2, '0');

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}T00:00:00${sign}${hours}:${minutes}`;
};

/**
 * Calculates age from a date string.
 * @param dateOfBirth - The date of birth string.
 * @returns The age in years
 */
export const calculateAgeFromString = (dateOfBirth: string): number => {
  const birthDate = dayjs.utc(dateOfBirth);
  const now = dayjs.utc();
  return now.diff(birthDate, 'year');
};

/**
 * Validates if the given string is a valid date in the format of DATE_FORMAT
 * @param dateString - The string to validate
 * @returns True if the string is a valid date, false otherwise
 */
export const validateDateString = (dateString: string): boolean => {
  return dayjs(dateString, DATE_FORMAT).isValid();
};

/**
 * Formats a date to the API format of 'YYYY-MM-DDTHH:mm:ssZZ'
 * Optionally sets the time to 00:00:00.000000
 * Throws an error if the date is invalid
 * @returns A string representing the date in the API format.
 * @param date
 * @param timeToZero
 * @param inputFormat
 */
export const dateToAPIFormat = (date: DayjsProps, timeToZero = true, inputFormat?: OptionType): string => {
  const formattedDate = dayjsCs(date, inputFormat);
  if (!formattedDate.isValid() || !date) {
    throw new Error('Invalid date provided');
  }
  // formattedDate.tz('Europe/Prague');
  if (timeToZero) {
    formattedDate.hour(0).minute(0).second(0).millisecond(0);
  }
  return formattedDate.format('YYYY-MM-DDTHH:mm:ssZZ');
};

/**
 * Optional version of dateToAPIFormat, returns undefined if the date is null or invalid
 * @returns A string representing the date in the API format or undefined if the date is null or invalid.
 * @param date
 * @param timeToZero
 * @param inputFormat
 */
export const dateToAPIFormatOptional = (date?: DayjsProps | null, timeToZero = true, inputFormat?: OptionType): string | undefined => {
  if (isValueEmpty(date)) {
    return undefined;
  }

  return dateToAPIFormat(date, timeToZero, inputFormat);
};
